import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFooter,
  CarouselIndicator,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Form,
} from '@qodo/design-system';
import { useUser } from 'hooks/useUser';
import { ClockFadingIcon, UserStarIcon } from 'lucide-react';
import { useStartMasterAppConsentMutation } from 'network/mutations/useStartMasterAppConsentMutation';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { refreshAccessToken } from 'utils/token';
import { spawn } from 'utils/translations';
import { Image } from '../../Image';
import { INSTALLATION_GUIDE_CAROUSEL_STEPS } from '../configuration';
import {
  AZURE_MASTER_APP_MANIFEST,
  AZURE_QODO_LOGO_PATH,
  FORM_IDS,
} from '../consts';
import { CarouselStepConfig } from '../types';
import { buildConsentState, storeConsentState } from '../utils';

export const InstallGuide = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const spawnStep = spawn('dialogs.azure-installation.steps.install-guide');
  const { user } = useUser();
  const { setIsPending } = useMultiStepDialog();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const startConsentMutation = useStartMasterAppConsentMutation();
  const methods = useForm();

  const onInstallAzureApp = async () => {
    const workspaceId = user?.workspaceId ?? user?.organizationId;

    if (!workspaceId) {
      console.warn('No workspace ID found for user, cannot start consent flow');

      return;
    }

    try {
      setIsPending(true);

      const state = buildConsentState(workspaceId);

      storeConsentState(state);

      const redirectUri = `${window.location.origin}${ROUTES.AZURE_INSTALLATION_STEP_3}`;

      const response = await startConsentMutation.mutateAsync({
        redirectUri,
        manifest: AZURE_MASTER_APP_MANIFEST,
        iconUrl: `${window.location.origin}${AZURE_QODO_LOGO_PATH}`,
      });

      storeConsentState({ ...state, backendState: response.state });

      // After starting the consent flow, refresh the access token so that if the user is redirected back to the app after consenting,
      // we have a valid token to check the consent status without forcing the user to sign in again
      await refreshAccessToken();

      window.location.href = response.consentUrl;
    } catch (err) {
      if (isMountedRef.current) {
        setIsPending(false);
      }

      console.warn('Failed to start consent flow:', err);
    }
  };

  const renderCarouselItem = (step: CarouselStepConfig) => {
    const title = spawnStep(`${step.id}.title`);
    const admin = spawnStep(`${step.id}.admin`);
    const note = spawnStep(`${step.id}.note`);

    return (
      <CarouselItem key={step.id}>
        <div className="flex gap-8">
          <Image
            source={step.image}
            alt={title}
            title={title}
            className="rounded-m m-auto w-62.5 h-auto"
          />
          <div>
            <div className="gap-4 items-center flex pt-4">
              <UserStarIcon className="size-4" />
              <span className="text-sm w-fit">{admin}</span>
            </div>
            <div className="text-sm gap-4 flex items-center pt-4">
              <ClockFadingIcon className="size-4" />
              <span className="text-sm w-fit">{note}</span>
            </div>
          </div>
        </div>
      </CarouselItem>
    );
  };

  const renderCarouselIndicator = (step: CarouselStepConfig, index: number) => (
    <CarouselIndicator
      key={step.id}
      index={index}
      isCurrent={index === current}
      className="uppercase p-0"
    >
      <span className="mr-0.5">{index + 1}.</span>
      {spawnStep(`${step.id}.title`)}
    </CarouselIndicator>
  );

  return (
    <Step title={spawnStep('title')} description={spawnStep('description')}>
      <Form
        {...({ id: FORM_IDS.INSTALL_GUIDE } as Record<string, string>)}
        methods={methods}
        onSubmit={onInstallAzureApp}
      >
        <div className="flex gap-6">
          <Carousel
            setApi={setApi}
            className="items-center rounded-md w-full bg-fill-interactive-neutral-idle px-5 py-4"
          >
            <CarouselContent>
              {INSTALLATION_GUIDE_CAROUSEL_STEPS.map(renderCarouselItem)}
            </CarouselContent>

            <CarouselFooter className="justify-between pt-4 gap-3!">
              <CarouselPrevious type="button" className="p-1.5 size-7" />
              <div className="flex gap-4 font-mono text-xs uppercase">
                {INSTALLATION_GUIDE_CAROUSEL_STEPS.map(renderCarouselIndicator)}
              </div>
              <CarouselNext type="button" className="p-1.5 size-7" />
            </CarouselFooter>
          </Carousel>
        </div>
      </Form>
    </Step>
  );
};
